import React, {useCallback, useEffect} from 'react';
import PropTypes from 'prop-types';
import TransitionGroup from 'react-transition-group/TransitionGroup';
import Transition from 'react-transition-group/Transition';
import cx from 'classnames';
import Loader from '@adsk/bim360-matrix-react-loader';

const loadMoreThreshold = 200;

const isBelowThreshold = container =>
  container.scrollHeight - container.scrollTop - container.clientHeight < loadMoreThreshold;

/**
 * InfiniteTransitionList
 * @param props
 * @returns {*}
 * @constructor
 */
export const InfiniteTransitionList = ({
  children,
  exitTimeout,
  className,
  loaderClassName,
  isLoading,
  hasMore,
  loadMore,
  container
}) => {
  const onScroll = useCallback(() => {
    if (isBelowThreshold(container) && !isLoading && hasMore) {
      loadMore();
    }
  }, [container, hasMore, isLoading, loadMore]);

  useEffect(() => {
    container?.addEventListener('scroll', onScroll);
    return () => {
      container?.removeEventListener('scroll', onScroll);
    };
  }, [container, onScroll]);

  if (!children.length) {
    return null;
  }

  const transitionTimeout = {
    // Set 'enter' timeout to '0' so that enter animation
    // will start immediately.
    enter: 0,

    // Set 'exit' timeout to transition duration + delay so that the 'exited'
    // status won't be applied until animation completes.
    exit: exitTimeout
  };

  return (
    <>
      <TransitionGroup>
        {React.Children.map(children, child => (
          <Transition timeout={transitionTimeout} key={child.key}>
            {status => {
              // Children is a function that receives the current
              // status of the animation.

              // Don't render anything if component has 'exited'.
              if (status === 'exited') {
                return null;
              }
              return React.cloneElement(child, {
                className: cx(child.props.className, `${className}__transition-animation--${status}`)
              });
            }}
          </Transition>
        ))}
      </TransitionGroup>
      {isLoading && <Loader className={loaderClassName} />}
    </>
  );
};

InfiniteTransitionList.displayName = 'InfiniteTransitionList';

InfiniteTransitionList.propTypes = {
  children: PropTypes.arrayOf(PropTypes.node),
  exitTimeout: PropTypes.number.isRequired,
  className: PropTypes.string.isRequired,
  loaderClassName: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  loadMore: PropTypes.func.isRequired,
  container: PropTypes.object
};

export default InfiniteTransitionList;
